import { Icon } from "@iconify/react";
import { useTranslation } from "@next-platform/client-common-core";
import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Radio } from "@nextui-org/react";
import { forwardRef, Key } from "react";

import Trans from "@/components/Trans/Trans";
import { getCurrencySymbol } from "@/utils";
import { cn } from "@/utils/cn";

import { PlanRadioProps } from "../types";

const PlanRadio = forwardRef<HTMLInputElement, PlanRadioProps>(
  ({ price, currency, billing_frequency, label, benefits = [], className, classNames = {}, ...props }, ref) => {
    const { t } = useTranslation();
    const periodLabelByBillingCycle: Record<BillingCycle, string> = {
      [BillingCycle.MONTHLY]: t("subscription:portal.perMonth"),
      [BillingCycle.QUARTERLY]: t("subscription:portal.perQuarter"),
      [BillingCycle.ANNUAL]: t("subscription:portal.perYear"),
    };
    const periodLabel = periodLabelByBillingCycle[billing_frequency ?? BillingCycle.MONTHLY] ?? t("subscription:portal.perMonth");
    return (
      <Radio
        {...props}
        ref={ref}
        classNames={{
          ...classNames,
          base: cn(
            "inline-flex m-0 px-3 py-4 max-w-[100%] items-center justify-between",
            "flex-row-reverse w-full cursor-pointer rounded-lg 3 border-medium border-default-100",
            "data-[selected=true]:border-secondary data-[selected=true]:bg-secondary-50",
            classNames?.base,
            className,
          ),
          wrapper: cn("group-data-[focus-visible=true]:ring-secondary", classNames?.wrapper),
          labelWrapper: cn("ml-0", classNames?.labelWrapper),
        }}
        color="secondary"
      >
        <div className="flex w-full items-center gap-1">
          <div className="item-center flex rounded-full bg-secondary-50 p-2 group-data-[selected=true]:bg-secondary-100"></div>
          <div className="flex w-full flex-col gap-1">
            <div className="flex items-center gap-1">
              <p className="text-small">{label}</p>
              <span className="mt-0.5 text-tiny text-default-500">
                {price !== undefined && (
                  <Trans
                    i18nKey="subscription:portal.pricePerPeriod"
                    values={{
                      price: `${getCurrencySymbol(currency)}${price}`,
                      period: periodLabel,
                    }}
                  />
                )}
              </span>
            </div>
            <ul>
              {benefits.map((benefit: string, index: Key) => (
                <li className="flex items-center gap-1" key={index}>
                  <Icon className="text-default-600" icon="ci:check" width={14} />
                  <p className="text-xs text-default-500">{benefit}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Radio>
    );
  },
);

PlanRadio.displayName = "PlanRadio";

export default PlanRadio;
